// §C (Addendum June 2026): Education Checklist admin UI — GA window config + item management.
// GA window boundaries stored at settings/educationChecklistConfig. Changes take effect immediately.
// Locked items (education-checklist-locked-items) and global customizable items
// (education-checklist-global-items) are top-level collections. Firestore rules restrict writes to HB
// super/lead admins; this UI mirrors that restriction at the data layer. Ordering within each GA
// window is managed by drag-and-drop (persisted to each item's sortOrder), not a manual number.

import { DragHandleIcon } from '@chakra-ui/icons'
import {
  Box,
  Button,
  Divider,
  Flex,
  HStack,
  IconButton,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Select,
  Spinner,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react'
import {
  DEFAULT_EDUCATION_CHECKLIST_CONFIG,
  EDUCATION_CHECKLIST_CONFIG_DOC,
  EDUCATION_CHECKLIST_GLOBAL_ITEMS,
  EDUCATION_CHECKLIST_LOCKED_ITEMS,
  EducationChecklistConfig,
  GaWindow,
  SETTINGS,
  WithId,
} from '@hb/shared'
import { DragDropContext, Draggable, DropResult, Droppable } from '@hello-pangea/dnd'
import {
  addDoc,
  collection,
  CollectionReference,
  deleteDoc,
  doc,
  orderBy,
  query,
  setDoc,
  writeBatch,
} from 'firebase/firestore'
import { useCallback, useMemo, useState } from 'react'
import { db } from '../../../../backend/db/db'
import { useDocument } from '../../../../backend/db/hooks'
import { useQuery } from '../../../../hooks/backend/useQuery'
import { SolidActionButton } from '../../../Buttons'

// ---------------------------------------------------------------------------
// Firestore document shape for checklist items (locked + global share this shape)
// ---------------------------------------------------------------------------
interface ChecklistItemDoc {
  text: string
  gaWindow: GaWindow
  sortOrder: number
  createdOn: number
}
type ChecklistItem = WithId<ChecklistItemDoc>

const GA_WINDOW_LABELS: Record<GaWindow, string> = {
  'first-trimester': 'First Trimester (<14w)',
  'second-trimester': 'Second Trimester (14–27w)',
  'third-trimester': 'Third Trimester (≥28w)',
  postpartum: 'Postpartum',
}

const GA_WINDOWS: GaWindow[] = [
  'first-trimester',
  'second-trimester',
  'third-trimester',
  'postpartum',
]

const formatGaLabel = (days: number) => `${Math.floor(days / 7)}w ${days % 7}d (${days}d)`

// ---------------------------------------------------------------------------
// GA window config panel — mirrors GbsConfigAdminView pattern exactly
// ---------------------------------------------------------------------------
const useEducationConfigAdmin = () => {
  const { data, loading } = useDocument<EducationChecklistConfig>(
    SETTINGS,
    EDUCATION_CHECKLIST_CONFIG_DOC,
  )

  const merged: EducationChecklistConfig = useMemo(
    () => ({ ...DEFAULT_EDUCATION_CHECKLIST_CONFIG, ...data }),
    [data],
  )

  const saveConfig = useCallback(
    async (
      updates: Partial<EducationChecklistConfig>,
    ): Promise<{ success?: string; error?: string }> => {
      const docRef = doc(db, SETTINGS, EDUCATION_CHECKLIST_CONFIG_DOC)
      try {
        await setDoc(docRef, { ...merged, ...updates }, { merge: true })
        return { success: 'Saved' }
      } catch (e) {
        return { error: (e as Error).message }
      }
    },
    [merged],
  )

  return { config: merged, loading, saveConfig }
}

const GaWindowConfigPanel = () => {
  const { config, loading, saveConfig } = useEducationConfigAdmin()
  const [firstEnd, setFirstEnd] = useState<string>(String(config.firstTrimesterEndDays))
  const [secondEnd, setSecondEnd] = useState<string>(String(config.secondTrimesterEndDays))
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState('')

  if (loading) {
    return (
      <Flex p={4} justify="center">
        <Spinner size="sm" />
      </Flex>
    )
  }

  const handleSave = async () => {
    setSaving(true)
    setMsg('')
    const first = Number(firstEnd)
    const second = Number(secondEnd)
    if (isNaN(first) || isNaN(second) || first < 1 || second <= first) {
      setMsg('Invalid values — first trimester end must be less than second trimester end')
      setSaving(false)
      return
    }
    const result = await saveConfig({
      firstTrimesterEndDays: first,
      secondTrimesterEndDays: second,
    })
    setMsg(result.success ?? result.error ?? '')
    setSaving(false)
  }

  const isDirty =
    Number(firstEnd) !== config.firstTrimesterEndDays ||
    Number(secondEnd) !== config.secondTrimesterEndDays

  return (
    <VStack align="stretch" spacing={4} maxW="420px">
      <Box>
        <Text fontSize="sm" fontWeight={600} color="gray.700" _dark={{ color: 'gray.200' }} mb={1}>
          First trimester end (last day inclusive)
        </Text>
        <Text fontSize="xs" color="gray.500" mb={2}>
          Items tagged 'first-trimester' appear when GA ≤ this value. Default: 97 days (13w+6d).
        </Text>
        <HStack>
          <NumberInput
            value={firstEnd}
            onChange={setFirstEnd}
            min={1}
            max={280}
            size="sm"
            w="100px">
            <NumberInputField />
            <NumberInputStepper>
              <NumberIncrementStepper />
              <NumberDecrementStepper />
            </NumberInputStepper>
          </NumberInput>
          <Text fontSize="xs" color="gray.500">
            days — {formatGaLabel(Number(firstEnd) || config.firstTrimesterEndDays)}
          </Text>
        </HStack>
      </Box>

      <Box>
        <Text fontSize="sm" fontWeight={600} color="gray.700" _dark={{ color: 'gray.200' }} mb={1}>
          Second trimester end (last day inclusive)
        </Text>
        <Text fontSize="xs" color="gray.500" mb={2}>
          Items tagged 'second-trimester' appear when GA is between first end + 1 and this value.
          Third trimester starts the following day. Default: 195 days (27w+6d).
        </Text>
        <HStack>
          <NumberInput
            value={secondEnd}
            onChange={setSecondEnd}
            min={1}
            max={300}
            size="sm"
            w="100px">
            <NumberInputField />
            <NumberInputStepper>
              <NumberIncrementStepper />
              <NumberDecrementStepper />
            </NumberInputStepper>
          </NumberInput>
          <Text fontSize="xs" color="gray.500">
            days — {formatGaLabel(Number(secondEnd) || config.secondTrimesterEndDays)}
          </Text>
        </HStack>
      </Box>

      <Flex align="center" gap={3} pt={1}>
        <SolidActionButton
          size="sm"
          onClick={handleSave}
          isLoading={saving}
          opacity={isDirty ? 1 : 0.5}>
          Save
        </SolidActionButton>
        {msg && (
          <Text fontSize="xs" color={msg.startsWith('Saved') ? 'green.500' : 'red.500'}>
            {msg}
          </Text>
        )}
      </Flex>
    </VStack>
  )
}

// ---------------------------------------------------------------------------
// Add-item form (shared by locked + global panels). New items append to the end
// of their GA window — sortOrder is assigned automatically, then tuned via drag.
// ---------------------------------------------------------------------------
const AddItemForm = ({
  collectionName,
  nextSortOrder,
}: {
  collectionName: string
  nextSortOrder: (window: GaWindow) => number
}) => {
  const [text, setText] = useState('')
  const [gaWindow, setGaWindow] = useState<GaWindow>('first-trimester')
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState('')

  const handleAdd = async () => {
    if (!text.trim()) {
      setMsg('Item text is required')
      return
    }
    setSaving(true)
    setMsg('')
    try {
      await addDoc(collection(db, collectionName), {
        text: text.trim(),
        gaWindow,
        sortOrder: nextSortOrder(gaWindow),
        createdOn: Date.now(),
      })
      setText('')
      setMsg('Added')
    } catch (e) {
      setMsg((e as Error).message)
    }
    setSaving(false)
  }

  return (
    <Box
      p={3}
      borderWidth="1px"
      borderRadius="md"
      borderColor="gray.200"
      _dark={{ borderColor: 'gray.600' }}>
      <Text fontSize="sm" fontWeight={600} mb={2}>
        Add Item
      </Text>
      <VStack align="stretch" spacing={2}>
        <Textarea
          size="sm"
          placeholder="Education topic text…"
          value={text}
          onChange={e => setText(e.target.value)}
          rows={2}
        />
        <HStack>
          <Select
            size="sm"
            value={gaWindow}
            onChange={e => setGaWindow(e.target.value as GaWindow)}
            w="auto">
            {GA_WINDOWS.map(w => (
              <option key={w} value={w}>
                {GA_WINDOW_LABELS[w]}
              </option>
            ))}
          </Select>
        </HStack>
        <Flex align="center" gap={2}>
          <SolidActionButton size="sm" onClick={handleAdd} isLoading={saving}>
            Add
          </SolidActionButton>
          {msg && (
            <Text fontSize="xs" color={msg === 'Added' ? 'green.500' : 'red.500'}>
              {msg}
            </Text>
          )}
        </Flex>
      </VStack>
    </Box>
  )
}

// ---------------------------------------------------------------------------
// Generic checklist-items panel with per-window drag-and-drop reordering.
// Dragging within a window rewrites sortOrder; dragging into another window
// also reassigns that item's gaWindow (reclassify a topic between trimesters).
// ---------------------------------------------------------------------------
const ChecklistItemsPanel = ({ collectionName }: { collectionName: string }) => {
  const itemsQuery = useMemo(
    () =>
      query(
        collection(db, collectionName) as CollectionReference<ChecklistItemDoc>,
        orderBy('gaWindow'),
        orderBy('sortOrder'),
      ),
    [collectionName],
  )
  const { dataArr, loading } = useQuery<ChecklistItemDoc>(itemsQuery)

  const grouped = useMemo(
    () =>
      GA_WINDOWS.reduce<Record<GaWindow, ChecklistItem[]>>(
        (acc, w) => {
          acc[w] = (dataArr ?? []).filter(i => i.gaWindow === w)
          return acc
        },
        {} as Record<GaWindow, ChecklistItem[]>,
      ),
    [dataArr],
  )

  // Persist a reordered window: write each item's new index as its sortOrder,
  // only touching docs whose position (or window) actually changed.
  const reindex = useCallback(
    (batch: ReturnType<typeof writeBatch>, items: ChecklistItem[], window: GaWindow) => {
      items.forEach((item, index) => {
        const windowChanged = item.gaWindow !== window
        if (item.sortOrder === index && !windowChanged) return
        batch.update(doc(db, collectionName, item.id), {
          sortOrder: index,
          ...(windowChanged ? { gaWindow: window } : {}),
        })
      })
    },
    [collectionName],
  )

  const handleDragEnd = useCallback(
    (result: DropResult) => {
      const { source, destination } = result
      if (!destination) return
      const from = source.droppableId as GaWindow
      const to = destination.droppableId as GaWindow
      if (from === to && source.index === destination.index) return

      const batch = writeBatch(db)
      if (from === to) {
        const items = [...(grouped[from] ?? [])]
        const [moved] = items.splice(source.index, 1)
        items.splice(destination.index, 0, moved)
        reindex(batch, items, to)
      } else {
        const fromItems = [...(grouped[from] ?? [])]
        const toItems = [...(grouped[to] ?? [])]
        const [moved] = fromItems.splice(source.index, 1)
        toItems.splice(destination.index, 0, moved)
        reindex(batch, fromItems, from)
        reindex(batch, toItems, to)
      }
      batch.commit().catch(console.error)
    },
    [grouped, reindex],
  )

  const handleDelete = useCallback(
    async (id: string) => {
      await deleteDoc(doc(db, collectionName, id))
    },
    [collectionName],
  )

  const nextSortOrder = useCallback(
    (window: GaWindow) => {
      const items = grouped[window] ?? []
      return items.length ? Math.max(...items.map(i => i.sortOrder ?? 0)) + 1 : 0
    },
    [grouped],
  )

  if (loading && !dataArr) {
    return (
      <Flex p={4} justify="center">
        <Spinner size="sm" />
      </Flex>
    )
  }

  return (
    <VStack align="stretch" spacing={4}>
      <DragDropContext onDragEnd={handleDragEnd}>
        {GA_WINDOWS.map(window => (
          <Box key={window}>
            <Text fontSize="xs" fontWeight={700} color="gray.500" mb={1} textTransform="uppercase">
              {GA_WINDOW_LABELS[window]}
            </Text>
            <Droppable droppableId={window}>
              {provided => (
                <VStack
                  align="stretch"
                  spacing={1}
                  minH="6px"
                  ref={provided.innerRef}
                  {...provided.droppableProps}>
                  {(grouped[window] ?? []).length === 0 ? (
                    <Text fontSize="xs" color="gray.400" pl={2}>
                      No items — drag one here or add below
                    </Text>
                  ) : (
                    (grouped[window] ?? []).map((item, index) => (
                      <Draggable key={item.id} draggableId={item.id} index={index}>
                        {(draggableProvided, snapshot) => (
                          <Flex
                            ref={draggableProvided.innerRef}
                            {...draggableProvided.draggableProps}
                            align="center"
                            justify="space-between"
                            gap={2}
                            py={1}
                            px={1}
                            borderRadius={4}
                            bg={snapshot.isDragging ? 'purple.50' : undefined}
                            _dark={{ bg: snapshot.isDragging ? 'purple.900' : undefined }}>
                            <HStack spacing={2} flex={1} minW={0}>
                              <IconButton
                                aria-label="Reorder item"
                                icon={<DragHandleIcon w={2.5} />}
                                size="xs"
                                variant="ghost"
                                colorScheme="gray"
                                {...draggableProvided.dragHandleProps}
                              />
                              <Text fontSize="sm">{item.text}</Text>
                            </HStack>
                            <Button
                              size="xs"
                              colorScheme="red"
                              variant="ghost"
                              onClick={() => handleDelete(item.id)}>
                              Delete
                            </Button>
                          </Flex>
                        )}
                      </Draggable>
                    ))
                  )}
                  {provided.placeholder}
                </VStack>
              )}
            </Droppable>
            <Divider mt={2} />
          </Box>
        ))}
      </DragDropContext>
      <AddItemForm collectionName={collectionName} nextSortOrder={nextSortOrder} />
    </VStack>
  )
}

// ---------------------------------------------------------------------------
// Top-level export
// ---------------------------------------------------------------------------
export const EducationChecklistConfigView = () => (
  <VStack align="stretch" spacing={4} p={4} h="100%" overflowY="auto">
    <Box>
      <Text fontFamily="Hero-New" fontWeight={700} fontSize="md">
        Education Checklist — GA Window Boundaries
      </Text>
      <Text fontSize="sm" color="gray.500" mt={1}>
        Controls which trimester window each education item falls into. Changes take effect
        immediately — no code deploy required.
      </Text>
    </Box>
    <GaWindowConfigPanel />

    <Divider />

    <Box>
      <Text fontFamily="Hero-New" fontWeight={700} fontSize="md">
        Locked Items
      </Text>
      <Text fontSize="sm" color="gray.500" mt={1}>
        Required education topics (HEDIS/ACNM/Medicaid audit). Cannot be removed by providers or
        practice admins. Drag to reorder within a window; drag across windows to reclassify.
      </Text>
    </Box>
    <ChecklistItemsPanel collectionName={EDUCATION_CHECKLIST_LOCKED_ITEMS} />

    <Divider />

    <Box>
      <Text fontFamily="Hero-New" fontWeight={700} fontSize="md">
        Global Customizable Items
      </Text>
      <Text fontSize="sm" color="gray.500" mt={1}>
        Optional topics pushed to every practice. Providers may remove these from their own list.
        Drag to reorder within a window; drag across windows to reclassify.
      </Text>
    </Box>
    <ChecklistItemsPanel collectionName={EDUCATION_CHECKLIST_GLOBAL_ITEMS} />
  </VStack>
)
